/**
 * Timestamp utilities for cart synchronization
 * Provides UTC timestamp generation and comparison functions
 */

/**
 * Generates a UTC timestamp in ISO 8601 format
 * @returns UTC timestamp string (e.g., "2024-12-24T10:30:45.123Z")
 */
export function generateTimestamp(): string {
  return new Date().toISOString()
}

/**
 * Compares two timestamps to determine if the first is newer than the second
 * @param timestamp1 - First timestamp to compare
 * @param timestamp2 - Second timestamp to compare
 * @returns true if timestamp1 is newer than timestamp2, false otherwise
 */
export function isNewerThan(timestamp1: string, timestamp2: string): boolean {
  try {
    // Check if inputs are valid strings
    if (typeof timestamp1 !== 'string' || typeof timestamp2 !== 'string') {
      return false
    }
    
    const date1 = new Date(timestamp1)
    const date2 = new Date(timestamp2)
    
    // Check if dates are valid
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      return false
    }
    
    return date1 > date2
  } catch {
    return false
  }
}

/**
 * Validates if a string is a valid ISO 8601 timestamp
 * @param timestamp - Timestamp string to validate
 * @returns true if timestamp is valid, false otherwise
 */
export function isValidTimestamp(timestamp: string): boolean {
  try {
    if (typeof timestamp !== 'string') {
      return false
    }
    
    const date = new Date(timestamp)
    
    // Check if date is valid and the string representation matches ISO format
    return !isNaN(date.getTime()) && date.toISOString() === timestamp
  } catch {
    return false
  }
}

/**
 * Sync result interface for debugging and monitoring
 */
export interface SyncResult {
  updated: boolean
  reason: 'client_newer' | 'server_newer' | 'no_change' | 'no_server_timestamp'
  clientTimestamp: string
  serverTimestamp?: string
}

/**
 * Determines sync action based on timestamp comparison
 * @param clientTimestamp - Client's last modified timestamp
 * @param serverTimestamp - Server's last modified timestamp (optional)
 * @returns SyncResult indicating what action should be taken
 */
export function determineSyncAction(
  clientTimestamp: string,
  serverTimestamp?: string
): SyncResult {
  // If server doesn't provide timestamp, use existing logic
  if (!serverTimestamp || !isValidTimestamp(serverTimestamp)) {
    return {
      updated: false,
      reason: 'no_server_timestamp',
      clientTimestamp
    }
  }
  
  // If client timestamp is invalid, update from server
  if (!isValidTimestamp(clientTimestamp)) {
    return {
      updated: true,
      reason: 'server_newer',
      clientTimestamp,
      serverTimestamp
    }
  }
  
  // Compare timestamps
  if (isNewerThan(serverTimestamp, clientTimestamp)) {
    return {
      updated: true,
      reason: 'server_newer',
      clientTimestamp,
      serverTimestamp
    }
  } else if (isNewerThan(clientTimestamp, serverTimestamp)) {
    return {
      updated: false,
      reason: 'client_newer',
      clientTimestamp,
      serverTimestamp
    }
  } else {
    return {
      updated: false,
      reason: 'no_change',
      clientTimestamp,
      serverTimestamp
    }
  }
}